import { forwardRef, useEffect, useState } from "react";
import ModalInputWorkflows from "./ModalInputWorkflows";
import SingleLoaderItem from "../Loaders/SingleLoaderItem";
import { useParams } from "react-router-dom";
import MandatorySign from "../Components/MandatorySign";
import { PiPlugsConnected } from "react-icons/pi";
import { MdClose } from "react-icons/md";

const TRIGGERS = [
    {
        value: "manual",
        label: "Trigger manually",
        description: "Triggers when a user manually starts the workflow."
    },
    {
        value: "schedule",
        label: "Trigger on schedule",
        description: "Runs automatically on a recurring schedule or a one-time scheduled time."
    }
];

const OTHER_NODES = [
    {
        value: "agent",
        label: "AI Agent",
        description: "Use an AI agent to analyze input, make decisions, and generate responses within your workflow."
    },
    {
        value: "plugin",
        label: "Call Plugin",
        description: "Execute an external integration or plugin to fetch data or trigger actions in connected services."
    },
    {
        value: "flow",
        label: "Flow Control",
        description: "Control the workflow logic with conditions, branching paths, loops, or filters."
    },
    {
        value: "function",
        label: "Custom Function",
        description: "Run custom code or predefined functions to transform data or perform specific operations."
    },
];

const SUBMENUS = {
    plugin: [],
    flow: [
        {
            value: "if",
            label: "If",
            description: "Run actions only if a condition is true."
        },
        {
            value: "if_else",
            label: "If / Else",
            description: "Branch into two paths depending on whether a condition is met."
        },
        {
            value: "if_else_if",
            label: "If / Else If",
            description: "Evaluate multiple conditions in sequence and run the first match."
        },
        {
            value: "switch",
            label: "Switch (Case)",
            description: "Route execution based on matching a value against multiple cases."
        },
        {
            value: "filter",
            label: "Filter",
            description: "Continue only if data matches specific conditions."
        },
        {
            value: "loop",
            label: "Loop",
            description: "Repeat actions for each item in a list."
        },
        {
            value: "while",
            label: "While Loop",
            description: "Repeat actions while a condition remains true."
        },
        {
            value: "break",
            label: "Break",
            description: "Exit the current loop early when a condition is met."
        },
        {
            value: "continue",
            label: "Continue",
            description: "Skip the current iteration and move to the next loop cycle."
        }
    ],
    function: [
        { value: "transform", label: "Transform Data", description: "Modify data structure" },
        { value: "custom", label: "Custom Code", description: "Run custom logic" },
    ],
};

const OverDropdown = forwardRef(({
    mcpData,
    fetchMcpData,
    loadingMcpData,

    openInputWorkflow,
    setOpenInputWorkflow,

    selectedItem,
    setSelectedItem,

    show,
    setOpenOverDropdown,
    typeNodeTrigger
}, ref) => {
    const { org_id } = useParams()

    const [activeMenu, setActiveMenu] = useState("root");
    const [submenuItems, setSubmenuItems] = useState([]);
    const [submenuTitle, setSubmenuTitle] = useState("");



    const handleSelect = (typeItem, item) => {
        const hasSubmenu = SUBMENUS[item.value];

        if (hasSubmenu) {
            setActiveMenu("submenu");
            setSubmenuItems(SUBMENUS[item.value]);
            setSubmenuTitle(item.label);
            return;
        }

        setOpenInputWorkflow(true);
        setSelectedItem(item);
    };

    const renderItem = (item) => {
        const hasSubmenu = SUBMENUS[item.value];

        return (
            <div
                key={item.value}
                className="hover_dropdown_inner_item"
                onClick={() => handleSelect(item.value, item)}
            >
                <div className="item_left">
                    <p className="item_label">{item.label}</p>
                    <p className="item_description">{item.description}</p>
                </div>

                {hasSubmenu && (
                    <i className="fa fa-chevron-right"></i>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (activeMenu === "submenu" && submenuTitle === "Call Plugin") {
            fetchMcpData?.(org_id);
        }
    }, [activeMenu, submenuTitle]);

    useEffect(() => {
        setActiveMenu("root")
        setSubmenuItems([])
        setSubmenuTitle("")
    }, [show]);

    return (
        <div
            ref={ref}
            className={`hover_dropdown ${show ? "hover_dropdown_show" : ""}`}
        >
            <div className="hover_dropdown_inner">

                <div className="hover_dropdown_inner_header">
                    <p className="hover_dropdown_inner_header_title">

                        {activeMenu === "submenu" && (
                            <span
                                style={{ marginRight: 8, cursor: "pointer" }}
                                onClick={() => setActiveMenu("root")}
                            >
                                ←
                            </span>
                        )}

                        {typeNodeTrigger
                            ? "Choose a trigger"
                            : activeMenu === "submenu"
                                ? submenuTitle
                                : "What happens next?"
                        }
                    </p>
                    <button className="new_automation_btn_inner" onClick={() => setOpenOverDropdown(false)}><MdClose /></button>
                </div>

                {typeNodeTrigger && (
                    <div className="hover_dropdown_inner_body fade-in">
                        {TRIGGERS.map(renderItem)}
                    </div>
                )}

                {!typeNodeTrigger && activeMenu === "root" && (
                    <div className="hover_dropdown_inner_body fade-in">
                        {OTHER_NODES.map(renderItem)}
                    </div>
                )}

                {!typeNodeTrigger && activeMenu === "submenu" && (
                    <div className="hover_dropdown_inner_body fade-in">

                        {submenuTitle === "Call Plugin" ? (
                            loadingMcpData ? (
                                <div>
                                    <SingleLoaderItem length={3} />
                                </div>
                            ) : (
                                mcpData.length > 0 ? (
                                    mcpData
                                        .map((mcp) => (
                                            <div
                                                key={mcp.id}
                                                className="hover_dropdown_inner_item"
                                                onClick={() => {
                                                    handleSelect("plugin", {
                                                        value: mcp.id,
                                                        label: mcp.name,
                                                        description: "Connected plugin"
                                                    });
                                                }}
                                            >
                                                <div className="item_left">
                                                    <p className="item_label">
                                                        {mcp.name}
                                                        {!mcp.is_connected && (
                                                            <button className="activate_mcp_btn"><PiPlugsConnected /></button>
                                                        )}
                                                    </p>
                                                    <p className="item_description">
                                                        {mcp.is_version_different && (
                                                            <span className="version_alert">
                                                                <MandatorySign message={"The deployed version and your connected version differ. We recommend reconnecting to sync them."} icon="info" />
                                                            </span>
                                                        )}
                                                        {mcp.description}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                ) : (
                                    <div className="empty_plugin fade-in">
                                        <p>No plugins available</p>
                                    </div>
                                )
                            )
                        ) : (
                            submenuItems.map(renderItem)
                        )}
                    </div>
                )}

            </div>

        </div>
    );
});

export default OverDropdown;